import { nonZeroNumber } from '../lib/format';
import { cloneElement, useLayoutEffect, useRef, useState, type CSSProperties, type HTMLAttributes, type ReactElement } from 'react';
import { createPortal } from 'react-dom';

// All overlays share a stack so only the top dialog owns keyboard focus.
const stack: HTMLElement[] = [];
const previousInert = new Map<HTMLElement, boolean>();
let previousOverflow = '';
function updateBackground(): void {
  const top = stack.at(-1);
  for (const node of Array.from(document.body.children)) {
    if (!(node instanceof HTMLElement)) continue;
    if (!previousInert.has(node)) previousInert.set(node, node.inert);
    node.inert = node !== top;
  }
  if (!top) {
    previousInert.forEach((inert, node) => { node.inert = inert; });
    previousInert.clear();
    document.body.style.overflow = previousOverflow;
  }
}

export interface DialogProps {
  label: string;
  onClose: () => void;
  children: ReactElement<HTMLAttributes<HTMLElement>>;
  pending?: boolean;
  dismissOnBackdrop?: boolean;
  style?: CSSProperties;
}

function mountDialog(portal: HTMLDivElement, callbacks: React.RefObject<{ onClose: () => void; pending: boolean }>): () => void {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    if (!stack.length) previousOverflow = document.body.style.overflow;
    document.body.appendChild(portal);
    stack.push(portal);
    portal.style.position = 'relative';
    const layer = nonZeroNumber(Number(getComputedStyle(document.documentElement).getPropertyValue('--layer-dialog'))) ?? 2000;
    portal.style.zIndex = String(layer + stack.length);
    document.body.style.overflow = 'hidden';
    updateBackground();
    const panel = portal.querySelector<HTMLElement>('[role="dialog"]')!;
    const focusable = (): HTMLElement[] => Array.from(panel.querySelectorAll<HTMLElement>('button, input, select, textarea, a[href], [tabindex]'))
      .filter(node => !node.matches(':disabled, [tabindex="-1"], input[type="hidden"]') && !node.closest('[hidden], [inert]') && getComputedStyle(node).display !== 'none' && getComputedStyle(node).visibility !== 'hidden');
    const focusFirst = (): void => (focusable()[0] ?? panel).focus();
    focusFirst();
    const onKey = (event: KeyboardEvent): void => {
      if (stack.at(-1) !== portal || event.defaultPrevented) return;
      if (event.key === 'Escape') {
        event.preventDefault(); event.stopPropagation();
        if (!callbacks.current.pending) callbacks.current.onClose();
      } else if (event.key === 'Tab') {
        const items = focusable(); const first = items[0]; const last = items.at(-1);
        if (!first) { event.preventDefault(); panel.focus(); }
        else if (event.shiftKey && (document.activeElement === first || document.activeElement === panel)) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && (document.activeElement === last || !panel.contains(document.activeElement))) { event.preventDefault(); first.focus(); }
      }
    };
    const onFocus = (event: FocusEvent): void => {
      if (stack.at(-1) === portal && !portal.contains(event.target as Node)) focusFirst();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('focusin', onFocus);
    return (): void => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('focusin', onFocus);
      const wasTop = stack.at(-1) === portal;
      const index = stack.indexOf(portal); if (index >= 0) stack.splice(index, 1);
      portal.remove(); updateBackground();
      if (wasTop && opener?.isConnected && !opener.closest('[inert]')) opener.focus();
    };

}

// The child remains the existing form/panel; the shell owns modal behavior.
export function Dialog({ label, onClose, children, pending = false, dismissOnBackdrop = false, style }: DialogProps): import("react").ReactPortal {
  const [portal] = useState((): HTMLDivElement => document.createElement('div'));
  const callbacks = useRef({ onClose, pending });
  useLayoutEffect((): void => { callbacks.current = { onClose, pending }; });
  useLayoutEffect(() => mountDialog(portal, callbacks), [portal]);

  return createPortal(<div className="modal-bg" style={style} onClick={event => {
    if (event.target === event.currentTarget && dismissOnBackdrop && !pending) onClose();
  }}>{cloneElement(children, { role: 'dialog', 'aria-modal': true, 'aria-label': label, 'aria-busy': pending, tabIndex: -1 })}</div>, portal);
}
